import { MODULES, singletonId, type ModuleId } from '@tahi/core';
import type { tiles } from '@tahi/db';
import type { Usable } from '../data/access';
import { getTileDef, listTileDefs, type TileDef, type TileSize } from './registry';

export type TileRow = typeof tiles.$inferSelect;

export const TODAY = 'today';

export const sizeClass: Record<TileSize, string> = {
  small: 'col-span-1',
  wide: 'col-span-2',
  tall: 'col-span-1 row-span-2 md:col-span-2',
};

export const SIZE_LABEL: Record<TileSize, string> = { small: 'Small', wide: 'Wide', tall: 'Tall' };

const MODULE_ORDER = Object.keys(MODULES) as ModuleId[];

/** Default tiles are per-user singletons (conventions §13, §15). */
export function defaultTileId(userId: string, dashboardId: string, type: string): string {
  return singletonId(userId, `tiles.${dashboardId}.${type}`);
}

export function visibleTiles(
  rows: readonly TileRow[],
  usable: Usable,
  opts: { includeHidden: boolean },
): TileRow[] {
  return rows
    .filter(
      (r) =>
        !r.deletedAt &&
        getTileDef(r.type) !== undefined &&
        (r.moduleId === 'today' || usable(r.moduleId as ModuleId)) &&
        (opts.includeHidden || !r.hidden),
    )
    .sort((a, b) => a.position - b.position || a.id.localeCompare(b.id));
}

export function positionBetween(before?: number, after?: number): number {
  if (before === undefined && after === undefined) return 1;
  if (before === undefined) return (after as number) - 1;
  if (after === undefined) return before + 1;
  return (before + after) / 2;
}

/** New position for tile `id` so it lands at `toIndex` in `list`; null if nothing moves. */
export function positionForMove(
  list: readonly TileRow[],
  id: string,
  toIndex: number,
): number | null {
  const from = list.findIndex((r) => r.id === id);
  if (from < 0 || toIndex < 0 || toIndex >= list.length || toIndex === from) return null;
  const others = list.filter((r) => r.id !== id);
  return positionBetween(others[toIndex - 1]?.position, others[toIndex]?.position);
}

export function nextPosition(rows: readonly TileRow[]): number {
  const live = rows.filter((r) => !r.deletedAt);
  return live.length === 0 ? 1 : Math.max(...live.map((r) => r.position)) + 1;
}

/** Tile config as an object, whether the column is json-mode or plain TEXT. */
export function tileConfig(row: Pick<TileRow, 'config'>): unknown {
  const raw = row.config as unknown;
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw);
    } catch {
      return {};
    }
  }
  return raw ?? {};
}

export function nextSize(def: TileDef, current: TileSize): TileSize {
  const i = def.sizes.indexOf(current);
  return def.sizes[(i + 1) % def.sizes.length] ?? def.defaultSize;
}

/** Default Today: each usable module's default tiles (module order); if none, the built-in Today tiles. */
export function defaultTodayDefs(usable: Usable): TileDef[] {
  const fromModules = listTileDefs()
    .filter((d) => d.defaultOnToday && d.moduleId !== 'today' && usable(d.moduleId))
    .sort((a, b) => MODULE_ORDER.indexOf(a.moduleId) - MODULE_ORDER.indexOf(b.moduleId));
  return fromModules.length > 0
    ? fromModules
    : listTileDefs().filter((d) => d.moduleId === 'today' && d.defaultOnToday);
}

export function groupDefsByModule(
  usable: Usable,
): { moduleId: ModuleId; name: string; defs: TileDef[] }[] {
  return MODULE_ORDER.filter((id) => id === 'today' || usable(id))
    .map((id) => ({
      moduleId: id,
      name: MODULES[id].name,
      defs: listTileDefs().filter((d) => d.moduleId === id),
    }))
    .filter((g) => g.defs.length > 0);
}
